/**
 * Durable storage, and whether the host promises to keep it.
 *
 * `MOBILE_SUPPLEMENT.md` 2.3 calls this "the highest-consequence platform risk
 * in the product", and it is right. iOS Safari evicts IndexedDB under storage
 * pressure or after a period of non-use, and the scorebook holds an entire game
 * locally before it syncs.
 *
 * This adapter deliberately does NOT wrap IndexedDB itself. `idb` already does
 * that well and the scorebook will use it directly. What has no good wrapper,
 * and what differs completely between a browser and a native shell, is the
 * DURABILITY question: may I keep this, and how much room is there. Under
 * Capacitor the queue moves to SQLite, where the answer is simply yes.
 */

import { browserNavigator, browserWindow, type Adapter } from './host.js';

export interface StorageEstimate {
  /** Bytes in use, or null when the host will not say. */
  readonly usedBytes: number | null;
  readonly quotaBytes: number | null;
}

export interface Storage extends Adapter {
  /**
   * Ask the host not to evict this origin's data.
   *
   * Called at scorebook initialization per the supplement. Browsers grant it
   * silently on an installed PWA and often refuse it in a plain tab, so a
   * refusal is NORMAL and must be surfaced quietly rather than as an alarm.
   */
  readonly requestPersistence: () => Promise<boolean>;
  readonly isPersisted: () => Promise<boolean>;
  readonly estimate: () => Promise<StorageEstimate>;
  /**
   * A small per-device value: what this device last saw, a preference. Not
   * for anything that must survive (the scorebook's queue is in IndexedDB).
   * Null when unset, or when the host has nowhere to keep one.
   */
  readonly readValue: (key: string) => string | null;
  /** True when kept. False, never a throw, when the host refused or has no store. */
  readonly writeValue: (key: string, value: string) => boolean;
}

interface StorageManagerLike {
  persist?: () => Promise<boolean>;
  persisted?: () => Promise<boolean>;
  estimate?: () => Promise<{ usage?: number; quota?: number }>;
}

function manager(): StorageManagerLike | null {
  const nav = browserNavigator();
  if (nav === null || !('storage' in nav)) return null;
  return nav.storage;
}

/** `localStorage`, or null: reading the property itself throws when a browser blocks it. */
function values(): globalThis.Storage | null {
  try {
    return browserWindow()?.localStorage ?? null;
  } catch {
    return null;
  }
}

export const storage: Storage = {
  // The CAPABILITY, not the object. A host with `navigator.storage` and no
  // `persist` reported support and then always returned false, which is the
  // same defect `network` was fixed for and ADR-0021 claimed was generalised.
  isSupported: () => typeof manager()?.persist === 'function',

  requestPersistence: async () => {
    const store = manager();
    if (store?.persist === undefined) return false;
    try {
      return await store.persist();
    } catch {
      return false;
    }
  },

  isPersisted: async () => {
    const store = manager();
    if (store?.persisted === undefined) return false;
    try {
      return await store.persisted();
    } catch {
      return false;
    }
  },

  estimate: async () => {
    const store = manager();
    if (store?.estimate === undefined) return { usedBytes: null, quotaBytes: null };
    try {
      const result = await store.estimate();
      return {
        usedBytes: result.usage ?? null,
        quotaBytes: result.quota ?? null,
      };
    } catch {
      return { usedBytes: null, quotaBytes: null };
    }
  },

  readValue: (key) => {
    try {
      return values()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },

  writeValue: (key, value) => {
    const store = values();
    if (store === null) return false;
    try {
      // Throws when full, and in some private windows.
      store.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },
};
